/*
 * Jira Ticket:
 * Created Date: Wed, 18th Mar 2020, 11:57:33 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useEffect } from 'react';
import {
  CREATE,
  Create,
  Confirm,
  fetchEnd,
  required,
  FileField,
  useNotify,
  FileInput,
  TextInput,
  fetchStart,
  SimpleForm,
  useRedirect,
  SelectInput,
  useTranslate,
  usePermissions,
  useDataProvider,
  SelectArrayInput,
  FormDataConsumer,
  ReferenceArrayInput,
  sanitizeListRestProps,
} from 'react-admin';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GetApp } from '@material-ui/icons';
import * as R from 'ramda';

import { grey } from 'theme';
import { columnStyles } from 'styles';
import { AccessDenied } from 'Layout';
import { isValidEmail } from 'utils/validation';
import getUploadInfo from 'utils/getUploadInfo';
import getRoleExplanation from 'utils/getRoleExplanation';
import { usePersistentOrganisation } from 'Components/Contexts';
import getOrganisationInfo from 'utils/getOrganisationInfo';
import getInviteSubcontractor from 'utils/getInviteSubcontractor';
import { InviteUserToolbar, ErrorModal } from 'Components';
import { getMaximumRole, isProjectManager } from 'utils/roles';
import { ActionButton as DownloadCSVButton } from '../../Components/Buttons';
import {
  userTypeOptions,
  inviteRoleOptions,
  inputMethodOptions,
  dynamicInviteRoleOptions,
} from 'utils/choices';

const emailValidation = value => {
  required();
  const emails = value?.split(',').filter(i => i);
  for (let i = 0; i < emails?.length; i++) {
    const test = isValidEmail(emails[i].trim());
    if (test === 'validation.invalidEmail') return 'validation.invalidEmail';
  }
};

const SanitizedForm = ({
  classes,
  currentRole,
  emailValidation,
  roleExplanation,
  organisationChoices,
  organisationId,
  setOrganisationId,
  organisationName,
  setOrganisationName,
  organisationCapabilities,
  showDownloadButton,
  inviteSubcontractor,
  setShowDownloadButton,
}) => {
  const [principalOrgId, setPrincipalOrgId] = useState(organisationId);
  const [projectsDisabled, setProjectsDisabled] = useState(
    !organisationId ? true : false
  );
  return (
    <>
      <div className={classes.root}>
        <div className={classes.wideColumn}>
          <SelectInput
            label="resources.Site.fields.organisation"
            source="organisationId"
            perPage={1000}
            choices={organisationChoices}
            initialValue={organisationId}
            onChange={event => {
              const orgName = organisationChoices.find(
                it => it.id === event.target.value
              ).name;

              setOrganisationId(event.target.value);
              setPrincipalOrgId(event.target.value);
              setOrganisationName(orgName);
              setProjectsDisabled(false);
            }}
            validate={required()}
          />
          <SelectInput
            source="type"
            validate={required()}
            choices={userTypeOptions.reverse()}
            defaultValue={'FREELANCER'}
            label={'resources.User.fields.whatAccess'}
          />
          <FormDataConsumer subscription={{ values: true }}>
            {({ formData }) =>
              formData.type === 'FREELANCER' ? (
                <ReferenceArrayInput
                  label={'resources.User.fields.whichProjects'}
                  source="projectIds"
                  resource="Project"
                  reference="Project"
                  filter={{ principalOrganisationId: principalOrgId }} // only display organisations we are contracted by
                  perPage={1000}
                  validate={required()}
                  sort={{ field: 'name', order: 'ASC' }}
                  disabled={projectsDisabled}
                >
                  <SelectArrayInput optionText="name" />
                </ReferenceArrayInput>
              ) : null
            }
          </FormDataConsumer>
          {/* limit role options to the user's current role or less, or, if they are inviting freelancers, Builder or Approver only */}
          <FormDataConsumer subscription={{ values: true }}>
            {({ formData }) => (
              <SelectInput
                source="role"
                validate={required()}
                choices={
                  formData.type === 'EMPLOYEE'
                    ? dynamicInviteRoleOptions(currentRole)
                    : inviteRoleOptions('APPROVER')
                }
                label={'resources.User.fields.roleLevel'}
              />
            )}
          </FormDataConsumer>
        </div>
        <div className={classes.flexGrowColumn}>
          <p style={{ marginTop: 8, color: grey }}>{roleExplanation}</p>
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.wideColumn}>
          <SelectInput
            source="multiUser"
            validate={required()}
            choices={inputMethodOptions}
            defaultValue={'MANUAL'}
            onChange={event =>
              event.target.value === 'BULK'
                ? setShowDownloadButton(true)
                : setShowDownloadButton(false)
            }
            label={'resources.User.fields.inviteMethod'}
          />
          <FormDataConsumer subscription={{ values: true }}>
            {({ formData }) =>
              formData.multiUser === 'MANUAL' && (
                <TextInput
                  source="email"
                  validate={emailValidation}
                  options={{ placeholder: 'user@example.com' }}
                  label={'resources.User.fields.inviteesEmail'}
                />
              )
            }
          </FormDataConsumer>
        </div>
        {showDownloadButton && (
          <>
            <div className={classes.column}>
              <Link
                to={
                  process.env.PUBLIC_URL +
                  '/files/multi_user_invite_template.csv'
                }
                target="_blank"
                className={classes.noUnderline}
                download
              >
                <DownloadCSVButton
                  label={'actions.downloadTemplate'}
                  variant="text"
                  icon={<GetApp />}
                />
              </Link>
            </div>
            {organisationCapabilities?.includes('subcontractor.invite') && (
              <div className={classes.flexGrowColumn} style={{ color: grey }}>
                {organisationId && (
                  <>
                    {inviteSubcontractor}
                    <Link
                      to={{
                        pathname: '/Subcontractor/create',
                        state: {
                          organisationId,
                          organisationName,
                        },
                      }}
                    >
                      here
                    </Link>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

function UploadTargetContainer({
  children,
  classes,
  hasShow,
  hasList,
  hasEdit,
  ...props
}) {
  return (
    <div className={classes.flexGrowColumn}>
      <div className={classes.fileUploadContainer}>{children}</div>
    </div>
  );
}

const EmploymentCreate = props => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dispatch = useDispatch();
  const classes = columnStyles();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();

  const [uploadInfo, setUploadInfo] = useState();
  const [roleExplanation, setRoleExplanation] = useState();
  const [dialogErrorText, setDialogErrorText] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalContent, setConfirmModalContent] = useState('');
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const { persistentOrganisation } = usePersistentOrganisation();
  const [organisationCapabilities, setOrganisationCapabilities] = useState();
  const [inviteSubcontractor, setInviteSubcontractor] = useState();
  const [organisationId, setOrganisationId] = useState(
    persistentOrganisation.id
  );
  const [organisationName, setOrganisationName] = useState();
  const [organisations, setOrganisations] = useState([]);
  const [organisationChoices, setOrganisationChoices] = useState([]);

  const currentRole = getMaximumRole(permissions, organisationId);

  const sanitizedFormProps = {
    ...props,
    classes,
    translate,
    currentRole,
    organisationId,
    setOrganisationId,
    organisationName,
    setOrganisationName,
    organisationChoices,
    organisationCapabilities,
    roleExplanation,
    emailValidation,
    showDownloadButton,
    inviteSubcontractor,
    setShowDownloadButton,
  };

  const toolBarProps = {
    ...props,
    notify,
    translate,
    organisationId,
    setShowErrorModal,
    setDialogErrorText,
    setFormDataToSubmit,
    setShowConfirmModal,
    setConfirmModalContent,
  };

  const getOrganisationOptions = () => {
    dispatch(fetchStart());
    dataProvider('GET_LIST', 'Organisation', {
      pagination: { page: 1, perPage: 1000 },
    })
      .then(result => {
        const orgs = R.path(['data'], result) || [];
        setOrganisations(orgs);
      })
      .catch()
      .finally(() => {
        dispatch(fetchEnd());
      });
    return () => {};
  };

  useEffect(() => {
    const choices = organisations
      .filter(org => isProjectManager(permissions, org.id))
      .map(permittedOrg => {
        return { id: permittedOrg.id, name: permittedOrg.name };
      });
    setOrganisationChoices(choices);
  }, [organisations, permissions]);

  const useMountEffect = fn => useEffect(fn, []);

  useMountEffect(getOrganisationOptions);

  useMountEffect(
    getRoleExplanation('invite_users_role_explanation', {
      dataProvider,
      dispatch,
      setRoleExplanation,
    })
  );

  useMountEffect(
    getUploadInfo('invite_users_upload_info', {
      dataProvider,
      dispatch,
      setUploadInfo,
    })
  );

  useMountEffect(
    getInviteSubcontractor('invite_users_invite_subcontractor', {
      dataProvider,
      dispatch,
      setInviteSubcontractor,
    })
  );

  useMountEffect(
    getOrganisationInfo(organisationId, {
      dataProvider,
      dispatch,
      notify,
      setOrganisationName,
      setOrganisationCapabilities,
    })
  );

  const callInviteUserMethod = () => {
    setShowConfirmModal(false);
    dispatch(fetchStart());

    if (formDataToSubmit) {
      delete formDataToSubmit.email;
      delete formDataToSubmit.inviteCSV;
      delete formDataToSubmit.multiUser;
      // NOTE: Confusingly, we are not creating a user here, we are actually creating an employment instance (inviting a user)
      return dataProvider(CREATE, 'Employment', formDataToSubmit)
        .then(data => {
          dispatch(fetchEnd());
          return redirect('/User');
        })
        .catch(error => {
          dispatch(fetchEnd());
          const {
            extensions: { code },
            message,
          } = error.graphQLErrors[0];
          if (code !== 'INTERNAL_SERVER_ERROR') {
            return notify(message, 'warning');
          }
          return notify('error.generic', 'warning');
        });
    } else {
      dispatch(fetchEnd());
      return notify('error.generic', 'warning');
    }
  };

  return isProjectManager(permissions, organisationId) ? (
    <Create
      title={
        organisationName && organisationName !== 'organisationId'
          ? `${translate(
              `resources.General.cmsTitle`
            )}${organisationName} - ${translate(
              'resources.User.fields.inviteUser'
            )}`
          : `${translate(`resources.General.cmsTitle`)} ${translate(
              'resources.User.fields.inviteUser'
            )}`
      }
      {...props}
    >
      <SimpleForm
        // submitOnEnter does not work so we override the onSubmit method too
        // see https://github.com/marmelab/react-admin/pull/2064 and https://github.com/marmelab/react-admin/issues/1797
        onSubmit={e => {
          e.preventDefault();
        }}
        submitOnEnter={false}
        toolbar={<InviteUserToolbar {...toolBarProps} />}
      >
        <SanitizedForm {...sanitizedFormProps} />
        <UploadTargetContainer classes={classes}>
          <FormDataConsumer subscription={{ values: true }}>
            {({ formData }) =>
           
              formData.multiUser === 'BULK' && (
                <FileInput
                  source="inviteCSV"
                  maxSize={100000}
                  validate={required()}
                  required={false}
                  multiple={false}
                  label={uploadInfo}
                  placeholder="Drag 'n' drop some files here, or click to select files"
                  accept=".csv"
                >
                  <FileField source="src" title="title" />
                </FileInput>
              )
            }
          </FormDataConsumer>
        </UploadTargetContainer>
        <Confirm
          isOpen={showConfirmModal}
          title={translate(
            'resources.User.fields.inviteUserConfirmModalTitle',
            { organisationName }
          )}
          content={confirmModalContent}
          onConfirm={callInviteUserMethod}
          onClose={() => setShowConfirmModal(false)}
        />
        <ErrorModal
          dialogOpen={showErrorModal}
          dialogTitle={translate(
            'resources.User.fields.inviteUserErrorModalTitle'
          )}
          dialogCopy={dialogErrorText}
          setDialogState={event => setShowErrorModal(event)}
          classes
          {...props}
        />
      </SimpleForm>
    </Create>
  ) : (
    <AccessDenied />
  );
};

export default EmploymentCreate;
